import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, ArrowRightLeft, FileSpreadsheet, Activity, ChevronRight, Layers, ShieldCheck, BarChart3, Truck } from 'lucide-react';
import styles from './InventoryPage.module.css';

const CARDS = [
    {
        path: '/inventory/overview', icon: Package, box: 'boxPrimary', tag: 'Live Data', title: 'Stock Overview', action: 'Open Overview',
        desc: 'See every product with stock, prices, stock value and status. Filter low or empty items and export to Excel.',
        points: ['Stock value at cost and selling price', 'Low and empty stock at a glance', 'CSV and Excel export'],
    },
    {
        path: '/inventory/update', icon: ArrowRightLeft, box: 'boxSuccess', tag: 'Passbook', title: 'Update & Movements', action: 'Record Movement',
        desc: 'Add purchases, record salon use, damage or returns, and change many products at once. Every change is saved in the stock history.',
        points: ['Stock in / out with reason and note', 'Purchase cost updates automatically', 'Bulk GST, HSN, category and usage'],
    },
    {
        path: '/inventory/vendors', icon: Truck, box: 'boxSuccess', tag: 'Suppliers', title: 'Vendors', action: 'Open Vendors',
        desc: 'Who supplies what, purchase bills, what you owe and what was returned. Every bill shows in Expenses as paid or pending.',
        points: ['Purchase bill adds stock and the expense', 'Udhaar, overdue and payments per vendor', 'Returns, refunds and cheque clearing'],
    },
    {
        path: '/inventory/analytics', icon: BarChart3, box: 'boxPrimary', tag: 'Insights', title: 'Inventory Analytics', action: 'View Analytics',
        desc: 'Daily stock in and out, products sold, profit, salon use, damage, and what to reorder before it runs out.',
        points: ['Daily in vs out and sales charts', 'Profit and margin per product', 'Reorder list with days of stock left'],
    },
    {
        path: '/inventory/add', icon: FileSpreadsheet, box: 'boxSuccess', tag: 'Setup', title: 'Add New Products', action: 'Add Products',
        desc: 'Add products in an Excel-style grid or upload a CSV / Excel file. Opening stock goes into the stock history.',
        points: ['Excel-style grid', 'Drag and drop CSV / Excel upload', 'Duplicate names are skipped'],
    },
];

const InventoryPage = () => {
    const navigate = useNavigate();

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.fixedHeader}>
                <div className={styles.titleRow}>
                    <div className={styles.badgeLabel}><Layers size={12} /> Inventory</div>
                    <h1 className={styles.pageTitle}>Inventory Management</h1>
                    <p className={styles.pageSubtitle}>Track stock, record every movement, and see what sells, what is used and what to reorder.</p>
                </div>
            </div>

            <div className={styles.contentArea}>
                <div className={styles.welcomeBanner}>
                    <div className={styles.bannerLeft}>
                        <div className={styles.bannerIconWrap}><Activity size={24} /></div>
                        <div>
                            <h2>Smiify Inventory</h2>
                            <p>Products sold on a bill are taken out of stock automatically. Record purchases, salon use and damage here, and check Analytics for daily movement.</p>
                        </div>
                    </div>
                    <div className={styles.bannerRight}>
                        <div className={styles.metricBadge}><ShieldCheck size={16} /> Auto stock from bills</div>
                    </div>
                </div>

                <div className={styles.cardsGrid}>
                    {CARDS.map(({ path, icon: Icon, box, tag, title, action, desc, points }) => (
                        <div
                            key={path}
                            className={styles.featureCard}
                            onClick={() => navigate(path)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(path); } }}
                        >
                            <div className={styles.cardTop}>
                                <div className={`${styles.iconBox} ${styles[box]}`}><Icon size={22} /></div>
                                <span className={styles.cardTag}>{tag}</span>
                            </div>
                            <h3 className={styles.cardTitle}>{title}</h3>
                            <p className={styles.cardDesc}>{desc}</p>
                            <ul className={styles.featureList}>
                                {points.map((p) => <li key={p}><ChevronRight size={13} /> {p}</li>)}
                            </ul>
                            <div className={styles.cardAction}><span>{action}</span><ChevronRight size={16} /></div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default InventoryPage;
